import { useDispatch, useSelector } from 'react-redux'
import { updateEntry } from 'apps/formsCreationKit/store/actions/sharedActions'
import { activeSheetSelector } from 'apps/builder/store/selectors'
import { alphaEntryTypesSelector, alphaKeyFieldTypesSelector } from 'apps/formsCreationKit/store/selectors/types'
import { eligibleForReferenceData } from 'apps/builder/lib/utils'
import { isLoopScreen } from 'apps/builder/components/workspace/sheets/helpers'
import { getTypeDisplayName } from 'apps/studio/lib/utils/entryTypes'
import { FieldIcon } from 'apps/studio/components/shared/FieldIcon'
import { GCDSDropdown, GCDSButtonDropdown } from '@gocanvas/gcds/components'

const choiceTypes = ['Dropdown', 'Single Choice', 'Multiple Choice']

export const SettingsFieldSelect = ({
  inputId,
  data,
  iconOnly,
  selectProps = {},
  label,
}) => {
  const {
    keyField,
    type,
    styles, // fitler out element data
    position, // filter out element version of this data
    page_number, // filter out element data
    entry_values: existingValues,
    reference_data_id: existingRefDataId,
    reference_data_column: existingRefDataColumn,
    dependent_entry: existingDependentEntry,
    ...entryRest
  } = data

  const dispatch = useDispatch()
  const activeSheet = useSelector(activeSheetSelector)
  const { id: sheetId, style: sheetStyle } = activeSheet || {}
  const selector = keyField
    ? alphaKeyFieldTypesSelector
    : alphaEntryTypesSelector
  const entryTypes = useSelector(selector) || []

  // TODO: this likely should get pulled out into a shared action since there are
  // rules around how to update the entry based on context that we likely would
  // benefit from having in one place.
  const update = ({ value }) => {
    const {
      type: newType,
      id,
      style,
      entry_values,
      ...typeRest
    } = entryTypes.find(({ type }) => type === value)
    const betweenChoices =
      choiceTypes.includes(type) && choiceTypes.includes(newType)

    // If the entry has reference data, we can only maintain that data
    // if the new entry type is eligible
    let refDataProps = {}
    if (
      (existingRefDataId || existingRefDataColumn) &&
      eligibleForReferenceData({ style, type: newType })
    ) {
      refDataProps = {
        reference_data_id: existingRefDataId,
        reference_data_column: existingRefDataColumn,
        dependent_entry: existingDependentEntry,
      }
    }
    const updatedEntry = {
      keyField,
      ...entryRest,
      type: newType,
      style,
      entry_type_id: id,
      ...typeRest,
      ...refDataProps,
      entry_values: betweenChoices ? existingValues : entry_values,
    }

    // if we're on a table sheet w/ style 2 and we change the keyfield from
    // Single Choice to anything else, we need to change the style to 1
    if (
      isLoopScreen(activeSheet) &&
      keyField &&
      sheetStyle == 2 &&
      type !== 'Single Choice'
    ) {
      updatedEntry.sheetPayload = { sheet_id: sheetId, style: 1 }
    }

    dispatch(updateEntry(updatedEntry))
  }

  const options = entryTypes.map(({ type, ...rest }) => ({
    value: type,
    label: getTypeDisplayName(type),
    type,
    IconComponent: () => <div className="item--icon"><FieldIcon type={type} /></div>,
    ...rest,
  }))

  const getFieldName = value => {
    const customFieldNames = {
      Sum: 'Calculation',
    }
    return customFieldNames[value] || value
  }

  const selectedValue = options.find(
    ({ value: optionValue }) =>
      optionValue === type || getFieldName(optionValue) === type
  )

  const SelectComponent = iconOnly ? GCDSButtonDropdown : GCDSDropdown
  const size = iconOnly ? 'medium' : 'large'

  return (
    <SelectComponent
      id={inputId}
      onChange={e => update({ value: e.value })}
      label={label}
      size={size}
      options={options}
      value={selectedValue}
      portal={true}
      className="settings-field-select"
      {...selectProps}
      inputType={'secondary'}
    />
  )
}
